import React, { useState, useCallback } from 'react'
import { Card, theme } from 'antd'
import { useSelector } from 'react-redux'
import GenAITabs from './GenAITabs'
import GenAIContent from './GenAIContent'
import GenAITemplateSelector from './GenAITemplateSelector'
import { assistantInitiate } from '../../../../../../../AI Tools/utils'
import { generateAIResponse } from '../../../../../../../AI Tools/assistants-lib'
import { AssistantEnums } from '../../../../../../../AI Tools/assitants-enums'
import { getLanguageName } from '../../utils/languageConfig'

const GenAIWorkspace = ({ originalTranscript, onInsertToNote, bookingData, userRef, selectedLanguage = 'en' }) => {
  const { token } = theme.useToken()
  const authState = useSelector((state) => state.auth)
  const [tabs, setTabs] = useState([
    {
      id: '1',
      title: 'New Tab',
      content: '',
      isGenerating: false,
      isTyping: false,
      generatedContent: null,
      rawAiResponse: '',
      showingTemplateSelector: true, // Initial tab shows template selector
      query: '' // Each tab has its own query state
    }
  ])
  const [activeTabId, setActiveTabId] = useState('1')

  const handleAddTab = () => {
    // Create a new tab with template selector interface (same as initial tab)
    const newTab = {
      id: Date.now().toString(),
      title: 'New Tab',
      content: '',
      isGenerating: false,
      isTyping: false,
      generatedContent: null,
      rawAiResponse: '',
      showingTemplateSelector: true, // New tabs also show template selector
      query: '' // Each new tab has its own query state
    }
    setTabs((prev) => [...prev, newTab])
    setActiveTabId(newTab.id)
  }

  const handleUpdateQuery = useCallback((tabId, query) => {
    setTabs((prev) => prev.map((tab) => (tab.id === tabId ? { ...tab, query } : tab)))
  }, [])

  const handleSelectTemplate = useCallback(
    (template) => {
      if (template.shouldGenerate) {
        // Generate content immediately for templates with queries
        handleGenerate(activeTabId, template.query, template.name)
      } else {
        // Just set the query in the tab
        setTabs((prev) =>
          prev.map((tab) =>
            tab.id === activeTabId
              ? {
                  ...tab,
                  query: template.query || '',
                  showingTemplateSelector: false,
                  title: template.name
                }
              : tab
          )
        )
      }
    },
    [activeTabId]
  )

  const activeTab = tabs.find((tab) => tab.id === activeTabId)

  const handleTabChange = (tabId) => {
    setActiveTabId(tabId)
  }

  const handleCloseTab = (tabId) => {
    if (tabs.length === 1) {
      // If it's the last tab, reset it instead of closing
      setTabs([
        {
          id: '1',
          title: 'New Tab',
          content: '',
          isGenerating: false,
          isTyping: false,
          generatedContent: null,
          rawAiResponse: '',
          showingTemplateSelector: true,
          query: ''
        }
      ])
      setActiveTabId('1')
      return
    }

    const newTabs = tabs.filter((tab) => tab.id !== tabId)
    setTabs(newTabs)

    // If we closed the active tab, switch to the first remaining tab
    if (tabId === activeTabId) {
      setActiveTabId(newTabs[0].id)
    }
  }

  const handleTabTitleChange = useCallback((tabId, newTitle) => {
    setTabs((prev) => prev.map((tab) => (tab.id === tabId ? { ...tab, title: newTitle } : tab)))
  }, [])

  const handleActiveTabQueryChange = useCallback(
    (query) => {
      handleUpdateQuery(activeTabId, query)
    },
    [activeTabId, handleUpdateQuery]
  )

  const handleGenerate = async (tabId, query, customTitle = null) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              content: query,
              isGenerating: true,
              isTyping: false,
              showingTemplateSelector: false,
              title: customTitle ? `Generating ${customTitle}...` : 'Generating...'
            }
          : tab
      )
    )

    try {
      // Create enhanced prompt with transcript context and medical professional info
      let enhancedPrompt = query

      // Prepare medical professional context - only for correspondence/letters
      const doctorInfo = authState?.agiliteUser
      let doctorContext = ''

      // Only include doctor signature for letters, referrals, and correspondence
      const needsDoctorSignature =
        query.toLowerCase().includes('letter') ||
        query.toLowerCase().includes('referral') ||
        query.toLowerCase().includes('correspondence') ||
        query.toLowerCase().includes('email')

      if (doctorInfo && needsDoctorSignature) {
        const firstName = doctorInfo.firstName || ''
        const lastName = doctorInfo.lastName || ''
        const phoneNumber = doctorInfo.phoneNumber || ''
        const profession = doctorInfo.extraData?.profession || 'medical professional'

        doctorContext = `

Medical Professional Information:
- Name: Dr. ${firstName} ${lastName}
- Contact: ${phoneNumber}
- Role: ${profession.charAt(0).toUpperCase() + profession.slice(1)}

Please sign off any correspondence or documents with the medical professional's name and contact information where appropriate.`
      }

      // Detect if user explicitly specified a language in their query
      const detectLanguageInQuery = (query) => {
        const queryLower = query.toLowerCase()

        // Check for explicit language requests
        const languagePatterns = [
          { pattern: /\bin english\b/, code: 'en', name: 'English' },
          { pattern: /\bin afrikaans\b/, code: 'af', name: 'Afrikaans' },
          { pattern: /\bin zulu\b|\bisi[zZ]ulu\b/, code: 'zu', name: 'Zulu' },
          { pattern: /\bin xhosa\b|\bisi[xX]hosa\b/, code: 'xh', name: 'Xhosa' },
          { pattern: /\bin sotho\b|\bsesotho\b/, code: 'st', name: 'Sotho' },
          { pattern: /\bin tswana\b|\bsetswana\b/, code: 'tn', name: 'Tswana' },
          { pattern: /\bin pedi\b|\bsepedi\b/, code: 'nso', name: 'Pedi' },
          { pattern: /\bin venda\b|\btshivenda\b/, code: 've', name: 'Venda' },
          { pattern: /\bin tsonga\b|\bxitsonga\b/, code: 'ts', name: 'Tsonga' },
          { pattern: /\bin swati\b|\bsiswati\b/, code: 'ss', name: 'Swati' },
          { pattern: /\bin ndebele\b|\bisindebele\b/, code: 'nr', name: 'Ndebele' },
          { pattern: /\bin french\b/, code: 'fr', name: 'French' },
          { pattern: /\bin spanish\b/, code: 'es', name: 'Spanish' },
          { pattern: /\bin german\b/, code: 'de', name: 'German' },
          { pattern: /\bin portuguese\b/, code: 'pt', name: 'Portuguese' },
          { pattern: /\bin dutch\b/, code: 'nl', name: 'Dutch' },
          { pattern: /\bin italian\b/, code: 'it', name: 'Italian' },
          { pattern: /\bin arabic\b/, code: 'ar', name: 'Arabic' },
          { pattern: /\bin chinese\b/, code: 'zh', name: 'Chinese' },
          { pattern: /\bin hindi\b/, code: 'hi', name: 'Hindi' },
          { pattern: /\bin urdu\b/, code: 'ur', name: 'Urdu' }
        ]

        for (const lang of languagePatterns) {
          if (lang.pattern.test(queryLower)) {
            return { code: lang.code, name: lang.name }
          }
        }
        return null
      }

      // Check if user specified a language in the query
      const explicitLanguage = detectLanguageInQuery(query)
      const finalLanguage = explicitLanguage ? explicitLanguage.code : selectedLanguage
      const finalLanguageName = explicitLanguage ? explicitLanguage.name : getLanguageName(selectedLanguage)
      const isEnglish = finalLanguage === 'en'

      // Debug logging
      if (explicitLanguage) {
        console.log(`Language override detected: "${explicitLanguage.name}" from query: "${query.substring(0, 50)}..."`)
      }

      if (originalTranscript && originalTranscript.trim()) {
        const languageContext = isEnglish
          ? `Based on the following English medical transcript, ${query}`
          : `Based on the following medical transcript, ${query}`

        enhancedPrompt = `${languageContext}

Original transcript: 
${originalTranscript}${doctorContext}

Please use the information from the transcript as context for generating the requested content. ${
          isEnglish ? 'Respond in English.' : `Please respond in ${finalLanguageName}.`
        }`
      } else if (doctorContext) {
        enhancedPrompt = `${query}${doctorContext}

${isEnglish ? 'Please respond in English.' : `Please respond in ${finalLanguageName}.`}`
      } else {
        enhancedPrompt = `${query}

${isEnglish ? 'Please respond in English.' : `Please respond in ${finalLanguageName}.`}`
      }

      // Use the same assistant ID as TranscribeAI for consistency
      const tmpData = await assistantInitiate(enhancedPrompt, AssistantEnums.assistant_ids.CLINICAL_NOTES)
      const aiResponse = await generateAIResponse(tmpData)
      const generatedContent = aiResponse?.data?.data[0]?.content[0]?.text?.value

      if (!generatedContent) {
        throw new Error('Failed to generate content. Please try again.')
      }

      setTabs((prev) =>
        prev.map((tab) => {
          if (tab.id === tabId) {
            return {
              ...tab,
              isGenerating: false,
              isTyping: true,
              rawAiResponse: generatedContent,
              showingTemplateSelector: false,
              title: customTitle || tab.title !== 'New Tab' ? customTitle || tab.title : 'Generated Content'
            }
          }
          return tab
        })
      )
    } catch (error) {
      console.error('Error generating content:', error)
      setTabs((prev) =>
        prev.map((tab) =>
          tab.id === tabId
            ? {
                ...tab,
                isGenerating: false,
                isTyping: false,
                generatedContent: `Error: ${error.message}`,
                showingTemplateSelector: false,
                title: 'Error'
              }
            : tab
        )
      )
    }
  }

  const handleTypingComplete = useCallback((tabId) => {
    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id === tabId) {
          return {
            ...tab,
            isTyping: false,
            generatedContent: tab.rawAiResponse,
            rawAiResponse: ''
          }
        }
        return tab
      })
    )
  }, [])

  const handleContentChange = useCallback((tabId, newContent) => {
    setTabs((prev) => prev.map((tab) => (tab.id === tabId ? { ...tab, generatedContent: newContent } : tab)))
  }, [])

  return (
    <Card
      className='genai-workspace'
      style={{
        height: 'calc(100vh - 150px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: 'none'
      }}
      bodyStyle={{
        flex: 1,
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <GenAITabs
        tabs={tabs}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
        onAddTab={handleAddTab}
        onCloseTab={handleCloseTab}
        onTabTitleChange={handleTabTitleChange}
      />

      {activeTab?.showingTemplateSelector ? (
        <GenAITemplateSelector
          key={activeTab.id}
          onSelectTemplate={handleSelectTemplate}
          query={activeTab.query}
          onQueryChange={handleActiveTabQueryChange}
        />
      ) : (
        <GenAIContent
          tab={activeTab}
          onGenerate={handleGenerate}
          onTypingComplete={handleTypingComplete}
          onContentChange={handleContentChange}
          onInsertToNote={onInsertToNote}
          bookingData={bookingData}
          userRef={userRef}
        />
      )}
    </Card>
  )
}

export default GenAIWorkspace
